import type {
  WeeklyReportCreatorOption,
} from '../../../api/weeklySearch';

export const UNKNOWN_CREATOR_LABEL = '不明なユーザー';

type SafeCreatorSelectionArgs = {
  selectedIds: string[];
  options: WeeklyReportCreatorOption[];
  knownUserIds?: Iterable<string>;
};

export type SafeCreatorSelection = {
  selectedValues: string[];
  selectedUserIds: string[];
  options: WeeklyReportCreatorOption[];
};

const normalizeUserId = (value: unknown): string =>
  String(value ?? '').trim().toLocaleLowerCase();

/**
 * 復元した発行者IDのうち、検索条件へ安全に変換できるものだけを返す。
 *
 * 旧キャッシュのwr_idなど、表示名もユーザーIDも復元できない値は検索APIへ
 * 送らない。これにより表示用の「不明なユーザー」が create_by 条件に混入して
 * 検索結果を0件にすることを防ぐ。
 */
export const buildSafeCreatorSearchSelection = ({
  selectedIds,
  options,
  knownUserIds = [],
}: SafeCreatorSelectionArgs): SafeCreatorSelection => {
  const optionById = new Map<string, WeeklyReportCreatorOption>();
  options.forEach((option) => {
    const id = String(option?.id ?? '').trim();
    const value = String(option?.value ?? '').trim();
    if (!id || !value || value === UNKNOWN_CREATOR_LABEL) return;
    if (!optionById.has(id)) optionById.set(id, option);
  });

  const knownUserIdByKey = new Map<string, string>();
  for (const rawUserId of knownUserIds) {
    const userId = String(rawUserId ?? '').trim();
    const key = normalizeUserId(userId);
    if (key && !knownUserIdByKey.has(key)) knownUserIdByKey.set(key, userId);
  }

  const selectedValues: string[] = [];
  const selectedUserIds: string[] = [];
  const resolvedOptions = new Map<string, WeeklyReportCreatorOption>();

  selectedIds.forEach((rawId) => {
    const id = String(rawId ?? '').trim();
    if (!id || selectedValues.includes(id)) return;

    const found = optionById.get(id);
    if (found) {
      selectedValues.push(id);
      resolvedOptions.set(id, found);
      const userId = String(found.user_id ?? '').trim();
      if (!found.legacy && userId && !selectedUserIds.includes(userId)) {
        selectedUserIds.push(userId);
      }
      return;
    }

    const canonicalUserId = knownUserIdByKey.get(normalizeUserId(id));
    if (!canonicalUserId) return;

    selectedValues.push(id);
    if (!selectedUserIds.includes(canonicalUserId)) {
      selectedUserIds.push(canonicalUserId);
    }
    resolvedOptions.set(id, {
      id,
      value: canonicalUserId,
      user_id: canonicalUserId,
      legacy: false,
    });
  });

  return {
    selectedValues,
    selectedUserIds,
    options: Array.from(resolvedOptions.values()),
  };
};
